/* eslint-disable no-underscore-dangle */
/* eslint-disable max-classes-per-file */
import {
  totalMarketsUOF,
  tennisNumberOfSetsMarketsUOF,
  spreadMarketsUOF,
} from 'constants/enum';
import { arrayToObject } from 'utils';
import {
  getMarketExtra,
  getMarketHcp,
  getOutcomeMarketMappingTbid,
  isMarketInCollection,
} from 'reducers/fishnetHelpers';

/**
 * Processes all outcomes from the given markets and formats them with additional properties.
 *
 * @param {Array<Object>} markets - Array of market objects containing outcomes and specifiers.
 * @returns {Array<Object>} - Array of formatted outcome objects.
 */
const allOutcomes = markets => markets.reduce((acc, cur) => {
  const outcomes = Object.values(cur.outcomes);
  const formattedOutcomes = outcomes.map((outcome, i) => {
    const oc = { ...outcome };
    oc.betstop = cur.betstop;

    if (isMarketInCollection(tennisNumberOfSetsMarketsUOF, cur._marketId)) {
      oc.name += ' '; // to avoid replacing '2' with teamname
    }

    // add hcp to outcome
    if (cur.specifiers && (cur.specifiers.hcp || cur.specifiers.total)) {
      if (isMarketInCollection(totalMarketsUOF, cur._marketId)) {
        oc.totalHcp = parseFloat(cur.specifiers.total);
      } else if (isMarketInCollection(spreadMarketsUOF, cur._marketId)) {
        oc.spreadHcp = i === 0 ? parseFloat(cur.specifiers.hcp) : -1 * parseFloat(cur.specifiers.hcp);
      }
    }

    if (cur.specifiers && (cur.specifiers.hcp && !cur.specifiers.hcp.includes(':'))) {
      oc.otherHcp = Math.abs(Number(cur.specifiers.hcp));
    } else {
      oc.otherHcp = null;
    }

    oc.marketId = cur._marketId; // required for over/under swap
    oc.tbid = getOutcomeMarketMappingTbid(
      cur._matchId,
      cur._marketId,
      oc._id,
    );

    return oc;
  });

  return acc.concat(formattedOutcomes);
}, []);

/**
 * Maps market IDs to arrays of outcome IDs for the given markets.
 *
 * @param {Array<Object>} markets - Array of market objects containing outcomes.
 * @returns {Object} - An object mapping market IDs to arrays of outcome IDs.
 */
const outcomeIdsByMarket = markets => markets.reduce((accumulator, cur) => {
  accumulator[cur._marketId] = Object.values(cur.outcomes).map(o => getOutcomeMarketMappingTbid(
    cur._matchId,
    cur._marketId,
    o._id,
  ));

  return accumulator;
}, {});

/**
 * Extracts market information and organizes it into an object keyed by market ID.
 *
 * @param {Array<Object>} markets - Array of market objects containing specifiers and betstop status.
 * @param {string} matchId - Unique identifier for the match.
 * @returns {Object} - An object mapping market IDs to market information.
 */
const marketInfo = (markets, matchId) => markets.reduce((accumulator, cur) => {
  const id = matchId + '_' + cur._marketId;
  const extra = getMarketExtra(cur.specifiers, cur._marketId);
  const hcp = getMarketHcp(cur._marketId, cur.specifiers, extra);

  accumulator[id] = {
    id,
    type: cur.type,
    extra,
    hcp,
    betstop: cur.betstop,
  };

  return accumulator;
}, {});

/**
 * Creates a lookup table for market information based on market IDs.
 *
 * @param {Array<Object>} markets - Array of market objects containing names and IDs.
 * @returns {Object} - An object mapping market IDs to market lookup information.
 */
const marketLookup = markets => markets.reduce((accumulator, cur) => {
  accumulator[cur._marketId] = {
    marketName: cur.name,
    marketNameShort: cur.nameShort ? cur.nameShort : cur.name,
    marketIdUOF: cur._marketId,
  };

  return accumulator;
}, {});

/**
 * Normalizes odds data for a given match and organizes it into a structured format.
 *
 * @param {Object} match - Object containing raw market data for the match (keyed by market ID).
 * @param {string} matchId - Unique identifier for the match.
 * @param {Object} [matchMarkets] - Optional object containing allowed markets configuration
 * from CMS or external odds panel source. Typically in the shape:
 * `{ response: { markets: Array<{ marketId: number }> } }`
 * Used to filter markets that should be displayed.
 * @param {Object} [betstops] - Optional object representing markets currently in betstop state.
 * Typically structured as:
 * `{ response: { matchId: string, markets: Array<{ marketId: number, isBetstop: boolean }> } }`.
 * @param {boolean} [hasOddsPanelConfig=false] - Whether the match uses a restricted odds panel configuration.
 * If `true`, only markets defined in `matchMarkets` are included.
 * @param {number|string} [periodNumber=1] - The current match period (set, quarter, or period) used
 * to filter markets based on specifiers like `setnr`, `quarternr`, or `periodnr`.
 *
 * @returns {Object} - Normalized odds data structured as:
 * ```js
 * {
 *   matches: { [matchId]: { oddsIdsByMarket: Object } },
 *   odds: Object, // keyed by tbid
 *   bigMarket: Object, // detailed market info
 *   marketLookupTable: Object // human-readable names
 * }
 * ```
 */
const normalizeOdds = (match, matchId, matchMarkets, betstops, hasOddsPanelConfig, periodNumber) => {
  // still block allowed match markets here temporary until rest is setup to handle it
  let updatedMarkets = Object.values(match);

  if (
    betstops
    && betstops.response
    && parseInt(betstops.response.matchId, 10) === parseInt(matchId, 10)
  ) {
    const betstopMap = betstops.response.markets.reduce((acc, cur) => {
      acc[cur.marketId] = cur.isBetstop;
      return acc;
    }, {});

    updatedMarkets = updatedMarkets.map(market => ({
      ...market,
      betstop: !!betstopMap[parseInt(market._marketId, 10)],
    }));
  }

  // Filter → Keep only markets where "active": true or "betstop": true.
  // Deduplicate by _marketId → If multiple exist for the same _marketId,
  // keep the one with the most balanced outcomes (closest probabilities to 50/50).
  const markets = Object.values(
    updatedMarkets
      .filter(market => {
        const isActive = market.active === true || market.betstop === true;
        const specifiers = market.specifiers || {};
        const periodStr = String(periodNumber);

        const { setnr, quarternr, periodnr } = specifiers;

        if (isActive) {
          // tennis, volleyball
          if (setnr) {
            return setnr === periodStr;
          }

          // basketball
          if (quarternr) {
            return quarternr === periodStr;
          }

          // ice hockey
          if (periodnr) {
            return periodnr === periodStr;
          }

          // No relevant specifier — include all active/betstop markets
          return true;
        }

        return false;
      })
      .reduce((acc, market) => {
        const marketId = market._marketId;

        const probs = market.outcomes.map(o => o.probability);

        // Calculate standard deviation of probabilities to assess balance
        const mean = probs.reduce((sum, p) => sum + p, 0) / probs.length;
        const variance = probs.reduce((sum, p) => sum + (p - mean) ** 2, 0) / probs.length;
        const stdDev = Math.sqrt(variance); // lower stdDev = more balanced

        if (!acc[marketId] || stdDev < acc[marketId].balance) {
          acc[marketId] = { market, balance: stdDev };
        }

        return acc;
      }, {}),
  ).map(entry => entry.market);

  let filteredMarkets;

  if (hasOddsPanelConfig && matchMarkets && matchMarkets.response) {
    const allowedMarketIds = matchMarkets.response.markets.map(market => market.marketId);
    filteredMarkets = markets.filter(market => allowedMarketIds.includes(Number(market._marketId)));
  }

  // If odds panel configuration is set in the LCR CMS, return only those markets if available
  const hasValidOddsPanel = hasOddsPanelConfig
    && matchMarkets
    && matchMarkets.response
    && Array.isArray(matchMarkets.response.markets)
    && matchMarkets.response.markets.length > 0;

  const marketsToUse = hasValidOddsPanel ? filteredMarkets : markets;

  const outcomes = allOutcomes(marketsToUse);
  const outcomeIds = outcomeIdsByMarket(marketsToUse);
  const mInfo = marketInfo((marketsToUse), matchId);
  const lookupTable = marketLookup(marketsToUse);

  // empty request also returns this just with empty objects
  return {
    matches: {
      [matchId]: {
        oddsIdsByMarket: outcomeIds,
      },
    },
    odds: arrayToObject(outcomes, 'tbid'),
    bigMarket: mInfo,
    marketLookupTable: lookupTable,
  };
};

export default normalizeOdds;
